import { useApolloClient } from "@apollo/client";
import { assert, getDataOrNull } from "@msys/common";
import { BookmarkAdd as BookmarkAddIcon } from "@mui/icons-material";
import { Button, ButtonProps } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../../commons/modals/ConfirmProcess.js";
import {
  QuoteTemplateSubscribeButton_QuoteTemplateFragment,
  useSubscribeToQuoteTemplate_HasOverridesLazyQuery,
  useSubscribeToQuoteTemplateMutation,
} from "./QuoteTemplateSubscribeButton.generated.js";

interface Props extends ButtonProps {
  doc: QuoteTemplateSubscribeButton_QuoteTemplateFragment;
  onAfterUpdate?: (
    opts: {
      docId: string;
      toVersionNumber: number;
    },
    applyOverrides: boolean
  ) => Promise<void> | void;
}

export const QuoteTemplateSubscribeButton = ({
  doc,
  onAfterUpdate,
  ...rest
}: Props) => {
  const { t } = useTranslate(["Global", "Templates", "TemplatesPublishing"]);

  const client = useApolloClient();
  const [fetch, query] = useSubscribeToQuoteTemplate_HasOverridesLazyQuery({
    client,
  });
  const [subscribe, { loading }] = useSubscribeToQuoteTemplateMutation({
    client,
  });

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);

  return (
    <>
      <Button
        loading={loading || query.loading}
        startIcon={
          rest.size !== "extra-small" ? <BookmarkAddIcon /> : undefined
        }
        variant={"contained"}
        color="primary"
        sx={{ whiteSpace: "nowrap" }}
        onClick={async e => {
          e.preventDefault();
          e.stopPropagation();

          assert(doc.templateSharing, "expected templateSharing");
          assert(
            !doc.templateSharing.subscribedToTemplate,
            "already subscribed"
          );
          assert(
            doc.templateSharing.latestSharedVersionNumber,
            "expected doc.templateSharing.latestSharedVersionNumber"
          );

          let applyOverrides = false;
          const result = await fetch({ variables: { templateId: doc.id } });
          assert(
            result?.data?.quoteTemplateHasOverrides,
            "expected quoteTemplateHasOverrides result"
          );
          const latestVersionWithOverrides = getDataOrNull(
            result?.data?.quoteTemplateHasOverrides
          )?.version;

          if (
            latestVersionWithOverrides &&
            latestVersionWithOverrides <
              doc.templateSharing.latestSharedVersionNumber
          ) {
            const result = await confirmProcessRef.current?.startConfirmProcess(
              {
                title: t("Applying overrides", { ns: "TemplatesPublishing" }),
                text: t(
                  "You have overrides from a previous version of this template. Do you want to apply them to this version?",
                  { ns: "TemplatesPublishing" }
                ),
                confirmButtonLabel: t("Yes", { ns: "Global" }),
                cancelButtonLabel: t("No", { ns: "Global" }),
              }
            );
            if (result) {
              applyOverrides = true;
            }
          }

          const latestSharedVersionNumber =
            doc.templateSharing.latestSharedVersionNumber;

          await subscribe({
            variables: {
              input: {
                quoteTemplateId: doc.id,
                versionNumber: latestSharedVersionNumber,
                applyOverrides,
              },
            },
          });

          await client.reFetchObservableQueries();
          await onAfterUpdate?.(
            {
              docId: doc.id,
              toVersionNumber: latestSharedVersionNumber,
            },
            applyOverrides
          );
        }}
        {...rest}
      >
        {t("Subscribe", { ns: "TemplatesPublishing" })}
      </Button>
      <ConfirmProcess ref={confirmProcessRef} />
    </>
  );
};
